import { createApi, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { Api } from "laravel-request";

import type { ApiResponse } from "./types/api.types";
import type { LaravelQueryBuilder } from "./types/laravelRequest";

export interface LaravelRequestQuery {
    resource: string;
    action: string;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    params?: Record<string, any>;
    data?: Record<string, any>;
    id?: string | number;
    queryBuilder?: (query: LaravelQueryBuilder) => LaravelQueryBuilder;
    useAll?: boolean;
}

export interface LaravelRequestError {
    status: number;
    data: any;
}

type LaravelRequestFailure = {
    status?: number;
    xhr?: { status?: number };
    responseData?: any;
    meta?: any;
};

const extractPayload = (response: ApiResponse<any> | unknown): any => {
    if (response != null && typeof response === "object" && "data" in response) {
        return (response as ApiResponse<any>).data;
    }
    return response;
};

const toLaravelError = (error: LaravelRequestFailure | unknown): LaravelRequestError => {
    const err = error as LaravelRequestFailure;
    return {
        status: err?.status || err?.xhr?.status || 500,
        data: err?.responseData ?? err?.meta ?? error ?? "Unknown error",
    };
};

const laravelRequestBaseQuery: BaseQueryFn<LaravelRequestQuery, unknown, LaravelRequestError> = async ({
    resource,
    action,
    method = "GET",
    params,
    data,
    id,
    queryBuilder,
    useAll,
}) => {
    try {
        return await new Promise<{ data: any }>((resolve, reject) => {
            let query = undefined as any as LaravelQueryBuilder;

            const hasValidId = id !== undefined && id !== null;

            if (method === "GET") {
                query = Api.get(resource, action, params) as any as LaravelQueryBuilder;
            } else if (method === "POST") {
                query = Api.post(resource, action, data || params) as any as LaravelQueryBuilder;
            } else if (method === "PUT" && hasValidId) {
                query = Api.putArg(resource, action, id, data || params) as any as LaravelQueryBuilder;
            } else if (method === "DELETE" && hasValidId) {
                query = Api.delete(resource, action, id, data) as any as LaravelQueryBuilder;
            } else {
                reject({
                    error: {
                        status: 400,
                        data: "Invalid method or missing id",
                    },
                });
                return;
            }

            if (queryBuilder) {
                query = queryBuilder(query);
            }

            const onSuccess = (response: ApiResponse<any>) => {
                resolve({ data: extractPayload(response) });
            };

            const onError = (error: LaravelRequestFailure) => {
                reject({ error: toLaravelError(error) });
            };

            if (useAll && typeof query.all === "function") {
                query.all(onSuccess, onError as (xhr: XMLHttpRequest, responseData?: any) => void);
            } else if (typeof query.first === "function" && queryBuilder && query.toString().includes("first")) {
                query.first(onSuccess, onError as (xhr: XMLHttpRequest, responseData?: any) => void);
            } else {
                query.call(onSuccess, onError as (xhr: XMLHttpRequest, responseData?: any) => void);
            }
        });
    } catch (error: any) {
        const wrapped = error as { error?: LaravelRequestError };
        return {
            error: wrapped.error ?? toLaravelError(error),
        };
    }
};

export const apiTags = {
    Application: "Application",
    Contact: "Contact",
    Client: "Client",
    Task: "Task",
    Service: "Service",
    Portfolio: "Portfolio",
    File: "File",
    Category: "Category",
    Hierarchy: "Hierarchy",
    Permission: "Permission",
    Settings: "Settings",
    Employee: "Employee",
    Funnel: "Funnel",
    /** Кэш RTK Query: invest-grid и связанные данные блока «Активы» воронки */
    FunnelActives: "FunnelActives",
    ClientValuation: "ClientValuation",
} as const;

export type ApiTag = (typeof apiTags)[keyof typeof apiTags];

export const baseApi = createApi({
    reducerPath: "api",
    baseQuery: laravelRequestBaseQuery,
    tagTypes: Object.values(apiTags),
    keepUnusedDataFor: 600,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: true,
    endpoints: () => ({}),
});
